import { useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, Coins, Landmark, Shield, Utensils } from 'lucide-react';
import {
  Card,
  CoverageBadge,
  DataTable,
  EffectiveValue,
  FilterBar,
  FilterDivider,
  MetricCard,
  MetricRow,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
  nextSort,
  RangePicker,
} from '../../components';
import { formatCents, formatCount, formatRate } from '../../lib/format/money';
import { defaultedSort, sortRows, type SortValue } from '../../lib/table/clientSort';
import { useTableUrlState, type TableSort } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import { listParam, pickParam } from '../profiles/people';
import type { HealthView, LocaleHealthCity, LocaleHealthRow } from './api';
import { rangeTitle, useAnalyticsWindow } from '../analytics/parts';
import { useLocaleHealth } from './queries';

/** Locale health and the locale page share one window: Month to date by default, in UTC days. */
export function useHealthWindow() {
  const w = useAnalyticsWindow({ range: 'mtd', utc: true });
  return { ...w, days: { start: w.window.startDate, end: w.window.endDate }, label: rangeTitle(w.range) };
}

/**
 * A city whose tax we are not fully collecting. A non-home-rule city's tax is
 * state-administered, so state-only is complete there; only a home rule city
 * needs its own registration.
 */
export function isUncovered(c: LocaleHealthCity): boolean {
  return c.isHomeRule ? c.coverage !== 'fully_covered' : c.coverage === 'uncollected';
}

const cents = (v: number) => formatCents(v);

/** Every row for the view is loaded at once, so the table sorts client-side. */
const SORT_VALUE: Record<string, SortValue<LocaleHealthRow>> = {
  locale: (r) => r.localeName.toLowerCase(),
  orders: (r) => r.orders,
  gross: (r) => r.grossCents,
  tax: (r) => r.taxCollectedCents,
  fees: (r) => r.feesCollectedCents,
  revenue: (r) => r.revenueFeeCents,
  buffer: (r) => r.bufferAccruedCents,
  alerts: (r) => r.openAlertCount,
};

/** Biggest gross food first when the URL names no sort. */
export const DEFAULT_SORT: TableSort = { key: 'gross', dir: 'desc' };

export const sortLocaleHealth = (rows: LocaleHealthRow[], sort: TableSort | null) =>
  sortRows(rows, sort, SORT_VALUE, (a, b) => a.localeName.localeCompare(b.localeName));

const COLUMNS: DataTableColumn<LocaleHealthRow>[] = [
  {
    id: 'locale',
    sortKey: 'locale',
    header: 'Locale',
    width: '1.6fr',
    cell: (r) => (
      <span className="min-w-0">
        <span className="block truncate font-semibold">{r.localeName}</span>
        <span className="block text-xs text-muted">
          {r.cities.length} {r.cities.length === 1 ? 'city' : 'cities'}
        </span>
      </span>
    ),
  },
  {
    id: 'orders',
    sortKey: 'orders',
    header: 'Orders',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.orders),
  },
  {
    id: 'gross',
    sortKey: 'gross',
    header: 'Gross food',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => cents(r.grossCents),
  },
  {
    id: 'tax',
    sortKey: 'tax',
    header: 'Tax',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => cents(r.taxCollectedCents),
  },
  {
    id: 'fees',
    sortKey: 'fees',
    header: 'Fees',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => cents(r.feesCollectedCents),
  },
  {
    id: 'revenue',
    sortKey: 'revenue',
    header: 'Revenue',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => cents(r.revenueFeeCents),
  },
  {
    id: 'buffer',
    sortKey: 'buffer',
    header: 'Buffer',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => <span className="font-semibold">{cents(r.bufferAccruedCents)}</span>,
  },
  {
    id: 'alerts',
    sortKey: 'alerts',
    header: 'Alerts',
    width: '80px',
    align: 'right',
    cell: (r) =>
      r.openAlertCount > 0 ? (
        <StatusPill tone="danger">{r.openAlertCount}</StatusPill>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
];

const COVERAGE_RANK: Record<string, number> = { uncollected: 0, state_only: 1, fully_covered: 2 };

const CITY_SORT_VALUE: Record<string, SortValue<LocaleHealthCity>> = {
  city: (c) => c.city.toLowerCase(),
  homeRule: (c) => (c.isHomeRule ? 1 : 0),
  coverage: (c) => COVERAGE_RANK[c.coverage],
  buffer: (c) => (c.bufferSource ? c.effectiveBufferPct : undefined),
  orders: (c) => c.transactionCount,
  accrued: (c) => c.bufferAccruedCents,
};

/** Busiest city first when the URL names no sort. */
export const CITY_DEFAULT_SORT: TableSort = { key: 'orders', dir: 'desc' };

export const sortCities = (cities: LocaleHealthCity[], sort: TableSort | null) =>
  sortRows(cities, sort, CITY_SORT_VALUE, (a, b) => a.city.localeCompare(b.city));

/** A sortable header cell, matching DataTable's (arrow shows the direction; a click cycles it). */
function SortTh({
  label,
  sortKey,
  sort,
  onSortChange,
  right,
}: {
  label: string;
  sortKey: string;
  sort?: TableSort;
  onSortChange?: (next: TableSort | null) => void;
  right?: boolean;
}) {
  const active = sort?.key === sortKey ? sort.dir : null;
  const thClass = `py-1 font-bold ${right ? 'text-right' : ''}`;
  if (!onSortChange) return <th className={thClass}>{label}</th>;
  return (
    <th className={thClass} aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}>
      <button
        type="button"
        onClick={() => onSortChange(nextSort(sort, sortKey))}
        className={`inline-flex items-center gap-1 border-0 bg-transparent p-0 text-inherit [font:inherit] [letter-spacing:inherit] [text-transform:inherit] hover:text-ink ${right ? 'flex-row-reverse' : ''}`}
      >
        <span>{label}</span>
        {active === 'asc' ? (
          <ArrowUp size={12} aria-hidden="true" />
        ) : active === 'desc' ? (
          <ArrowDown size={12} aria-hidden="true" />
        ) : (
          <ArrowUpDown size={12} aria-hidden="true" className="opacity-40" />
        )}
      </button>
    </th>
  );
}

/**
 * A locale's cities with orders. Pass `sort` and `onSortChange` to make the
 * headers sortable; the rows are then shown in that order.
 */
export function CityRows({
  cities,
  sort,
  onSortChange,
}: {
  cities: LocaleHealthCity[];
  sort?: TableSort;
  onSortChange?: (next: TableSort | null) => void;
}) {
  if (!cities.length) return <div className="text-[13px] text-muted">No city could be resolved for these orders.</div>;
  const th = { sort, onSortChange };
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[.14em] text-muted">
          <SortTh label="City" sortKey="city" {...th} />
          <SortTh label="Home rule" sortKey="homeRule" {...th} />
          <SortTh label="Coverage" sortKey="coverage" {...th} />
          <SortTh label="Effective buffer" sortKey="buffer" {...th} />
          <SortTh label="Orders" sortKey="orders" right {...th} />
          <SortTh label="Buffer accrued" sortKey="accrued" right {...th} />
        </tr>
      </thead>
      <tbody>
        {(sort ? sortCities(cities, sort) : cities).map((c) => (
          <tr
            key={c.cityId}
            className={`border-t border-[rgba(143,68,32,.08)] ${c.unconfigured ? 'bg-[rgba(178,58,46,.05)]' : ''}`}
          >
            <td className="py-2">
              <span className="font-semibold">{c.city}</span>
              <span className="text-muted">, {c.stateProvince}</span>
              {c.unconfigured && (
                <span className="ml-2">
                  <StatusPill tone="danger">Not reviewed</StatusPill>
                </span>
              )}
            </td>
            <td className="py-2 text-muted">{c.isHomeRule ? 'Yes' : 'No'}</td>
            <td className="py-2">
              <CoverageBadge coverage={c.coverage} homeRule={c.isHomeRule} />
            </td>
            <td className="py-2">
              {c.bufferSource ? (
                <EffectiveValue value={formatRate(c.effectiveBufferPct, 'fraction')} source={c.bufferSource} />
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td className="py-2 text-right tabular-nums">{formatCount(c.transactionCount)}</td>
            <td className="py-2 text-right tabular-nums">{cents(c.bufferAccruedCents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The four money cards, summed over the rows given. */
export function HealthMetrics({
  rows,
  rangeLabel,
  loading,
  failed,
}: {
  rows: LocaleHealthRow[];
  rangeLabel: string;
  loading: boolean;
  failed: boolean;
}) {
  const sum = (f: (r: LocaleHealthRow) => number) => rows.reduce((a, r) => a + f(r), 0);
  const n = (v: number) => (loading ? '…' : failed ? '—' : cents(v));
  return (
    <MetricRow>
      <MetricCard
        label="Gross food sales"
        value={n(sum((r) => r.grossCents))}
        hint={rangeLabel}
        accent="var(--admin-chart-1)"
        icon={<Utensils size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Tax collected"
        value={n(sum((r) => r.taxCollectedCents))}
        hint="state + county + city"
        accent="var(--admin-chart-2)"
        icon={<Landmark size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Buffer accrued"
        value={n(sum((r) => r.bufferAccruedCents))}
        hint="reserved against uncollected tax"
        accent="var(--admin-chart-4)"
        icon={<Shield size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Revenue fee"
        value={n(sum((r) => r.revenueFeeCents))}
        hint="the round-up"
        accent="var(--admin-chart-3)"
        icon={<Coins size={15} aria-hidden="true" />}
      />
    </MetricRow>
  );
}

/** The not-yet-reviewed (BE `unconfigured`) / not-fully-covered cards for the cities given. */
export function CityFlags({ cities }: { cities: LocaleHealthCity[] }) {
  const unconfigured = cities.filter((c) => c.unconfigured);
  const uncovered = cities.filter(isUncovered);
  if (!unconfigured.length && !uncovered.length) return null;
  return (
    <div className="grid grid-cols-1 gap-3.5 desk:grid-cols-2">
      {unconfigured.length > 0 && (
        <Card surface="panel">
          <div className="mb-1.5 text-[13px] font-semibold text-danger">
            {unconfigured.length} new {unconfigured.length === 1 ? 'city' : 'cities'} not yet reviewed
          </div>
          <div className="text-[13px] text-muted">
            {unconfigured.map((c) => c.city).join(' · ')} — first seen in order addresses; using the locale's defaults.
          </div>
        </Card>
      )}
      {uncovered.length > 0 && (
        <Card surface="panel">
          <div className="mb-1.5 text-[13px] font-semibold text-danger">
            {uncovered.length} {uncovered.length === 1 ? 'city' : 'cities'} not fully covered
          </div>
          <div className="text-[13px] text-muted">
            {uncovered.map((c) => c.city).join(' · ')} — city tax is not being collected; the buffer accrues against it.
          </div>
        </Card>
      )}
    </div>
  );
}

const VIEWS: HealthView[] = ['orders', 'pricing', 'pricing_off', 'configured', 'unconfigured'];
const VIEW_LABEL: Record<HealthView, string> = {
  orders: 'With orders',
  pricing: 'Pricing on',
  pricing_off: 'Pricing off',
  configured: 'Configured',
  unconfigured: 'Unconfigured',
};
const EMPTY_LABEL: Record<HealthView, string> = {
  orders: 'No orders in this range.',
  pricing: 'No locale has pricing on.',
  pricing_off: 'Every locale has pricing on.',
  configured: 'No locale has its own settings.',
  unconfigured: 'Every locale has its own settings.',
};

/**
 * Locale health (kit `LocaleHealthScreen`; §7.10): every locale's money for the
 * range. A row opens the locale's own page — its cities and its levers. The
 * view chips pick which locales: with orders in the range (default), or —
 * orders or not, at zero — those with pricing on or off (price cap above or
 * at $0), with their own settings, or without them.
 */
export function LocaleHealthScreen() {
  const table = useTableUrlState();
  const navigate = useNavigate();
  const w = useHealthWindow();
  const view = pickParam(table.filters.view, VIEWS) ?? 'orders';
  const localeIds = listParam(table.filters.localeIds);
  const q = useLocaleHealth({ days: w.days, localeIds, view });
  const { sort, onSortChange } = defaultedSort(table.sort, DEFAULT_SORT, table.setSort);
  const rows = sortLocaleHealth(q.data?.rows ?? [], sort);

  return (
    <div className="flex flex-col gap-[18px]">
      <FilterBar>
        <RangePicker value={w.range} onChange={w.setRange} />
        <FilterDivider />
        <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
        <span className="text-xs text-muted">Days are UTC.</span>
      </FilterBar>
      <FilterBar>
        {VIEWS.map((v) => (
          <SelectableChip
            key={v}
            active={view === v}
            onClick={() => table.setFilter('view', v === 'orders' ? undefined : v)}
          >
            {VIEW_LABEL[v]}
          </SelectableChip>
        ))}
      </FilterBar>

      <HealthMetrics rows={rows} rangeLabel={w.label} loading={q.isPending} failed={q.isError} />

      <CityFlags cities={rows.flatMap((r) => r.cities)} />

      <DataTable
        label="Locale health"
        minWidth={1080}
        columns={COLUMNS}
        rows={rows}
        getRowId={(r) => r.localeId}
        onRowClick={(r) =>
          void navigate({
            to: `/money/locale-health/${r.localeId}` as never,
            search: (w.range === 'mtd' ? {} : { range: w.range }) as never,
          })
        }
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        sort={sort}
        onSortChange={onSortChange}
        emptyLabel={EMPTY_LABEL[view]}
      />
    </div>
  );
}
